// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { clearSearchHistory, getSearchHistory } from "@insite/client-framework/Services/AutocompleteService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import { IconMemo, IconPresentationProps } from "@insite/mobius/Icon";
import Clock from "@insite/mobius/Icons/Clock";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { color, xScreen } from "@werner/XDL/Utilities";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    focusedQuery?: string;
    goToUrl: (url: string) => void;
    extendedStyles?: SearchHistoryStyles;
    // XNG-Changes
    width: number;
}

const mapStateToProps = (state: ApplicationState) => ({
    searchHistoryEnabled: getSettingsCollection(state).searchSettings.searchHistoryEnabled,
    searchPath: getSettingsCollection(state).searchSettings.searchPath,
});

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface SearchHistoryStyles {
    headerText?: TypographyPresentationProps;
    wrapper?: InjectableCss;
    itemWrapper?: InjectableCss;
    icon?: IconPresentationProps;
    link?: LinkPresentationProps;
    focusedLink?: LinkPresentationProps;
    clearLink?: LinkPresentationProps;
    // XNG-Changes
    titleWrapper?: InjectableCss;
}

export const searchHistoryStyles: SearchHistoryStyles = {
    headerText: {
        weight: 500,
        css: css`
            text-align: left;
            margin: 0;
        `,
    },
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            padding: 16px;
            width: ${({ width }: any) => width}px;
            ${xScreen("lte", "sm")} {
                width: 100%;
            }
        `,
    },
    itemWrapper: {
        css: css`
            display: flex;
            align-items: center;
        `,
    },
    icon: {
        src: Clock,
        css: css`
            margin-right: 10px;
        `,
    },
    link: {
        typographyProps: {
            ellipsis: true,
            css: css`
                width: 100%;
                text-align: left;
                margin: 5px 0;
            `,
        },
        css: css`
            width: 100%;
            overflow: hidden;
        `,
    },
    focusedLink: {
        typographyProps: {
            ellipsis: true,
            css: css`
                width: 100%;
                text-align: left;
                margin: 5px 0;
            `,
        },
        css: css`
            width: 100%;
            overflow: hidden;
            background-color: ${color("common.ui2")};
        `,
    },
    clearLink: {
        typographyProps: {
            css: css`
                width: 100%;
                text-align: right;
            `,
        },
    },
    titleWrapper: {
        css: css`
            display: flex;
            justify-content: space-between;
            align-items: center;
            border-bottom: 1px solid ${color("common.ui3")};
            margin-bottom: 16px;
            padding-bottom: 12px;
        `,
    },
};

const styles = searchHistoryStyles;

class SearchHistory extends React.Component<Props> {
    private readonly styles: SearchHistoryStyles;

    constructor(props: Props) {
        super(props);

        this.styles = mergeToNew(styles, props.extendedStyles);
    }

    clearLinkClickHandler = () => {
        clearSearchHistory();
        this.forceUpdate();
    };

    // XNG-Changes: change structure
    render() {
        if (!this.props.searchHistoryEnabled) {
            return null;
        }

        const searchHistory = getSearchHistory();
        if (searchHistory.length === 0) {
            return null;
        }

        const styles = this.styles;
        return (
            <StyledWrapper {...styles.wrapper} {...{ width: this.props.width }}>
                <StyledWrapper {...styles.titleWrapper}>
                    <Typography {...styles.headerText}>{translate("Search History")}</Typography>
                    <Link {...styles.clearLink} onClick={this.clearLinkClickHandler} aria-label="Clear Search History">
                        {translate("Clear Search History")}
                    </Link>
                </StyledWrapper>
                {searchHistory.map(searchHistoryItem => (
                    <StyledWrapper {...styles.itemWrapper} key={searchHistoryItem.query}>
                        {/* // XNG-Changes - remove clock icon
                        <IconMemo {...styles.icon} /> */}
                        <Link
                            {...(this.props.focusedQuery === searchHistoryItem.query
                                ? styles.focusedLink
                                : styles.link)}
                            onClick={() => {
                                if (searchHistoryItem.redirectUri) {
                                    this.props.goToUrl(searchHistoryItem.redirectUri);
                                } else {
                                    this.props.goToUrl(
                                        `/${this.props.searchPath}?query=${encodeURIComponent(
                                            searchHistoryItem.query,
                                        )}`,
                                    );
                                }
                            }}
                        >
                            {searchHistoryItem.query}
                        </Link>
                    </StyledWrapper>
                ))}
            </StyledWrapper>
        );
    }
}

export default connect(mapStateToProps)(SearchHistory);
